import React, { useEffect, useState } from "react";
import { Modal, Stack, Text, Textarea } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import { ApiErrorAlert } from "./ApiErrorAlert";

export interface NewDraftDialogProps {
  opened: boolean;
  /** Version the draft copies; `null` = the active version (the API default). */
  baseVersionId: string | null;
  onClose: () => void;
  onSubmit: (comment: string) => Promise<void>;
}

/** A new draft starts as a copy of its base (no changes yet); editing happens on the edit page. */
export const NewDraftDialog: React.FC<NewDraftDialogProps> = ({ opened, baseVersionId, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (opened) {
      setComment("");
      setError(null);
    }
  }, [opened]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(comment.trim());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("mappingConfig.newDraft.title")}
           portalProps={{ target: "#admin-portal-root" }}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {baseVersionId ? t("mappingConfig.newDraft.fromVersion", { id: baseVersionId }) : t("mappingConfig.newDraft.fromActive")}
        </Text>
        <Textarea label={t("mappingConfig.comment")} value={comment} onChange={(e) => setComment(e.currentTarget.value)}
                  autosize minRows={2} maxLength={2000} />
        {error !== null && <ApiErrorAlert error={error} />}
        <Stack align="flex-end">
          <Button onClick={submit} loading={busy}>{t("mappingConfig.newDraft.create")}</Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
